import React from "react";
import {
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Card,
  CardBody,
  ButtonToolbar,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import { useHistory } from "react-router";
import { toast } from "react-toastify";
import {
  GET_ALL_DEPARTMENT,
  GET_ALL_KPI_BY_DEPARTMENT_ID,
  GET_DEPARTMENT_KPI_BY_ID,
} from "@/GraphQL/Queries";
import {
  CREATE_DEPARTMENT_KPI,
  UPDATE_DEPARTMENT_KPI,
} from "@/GraphQL/Mutation";
import CkEditorInput from "@/containers/Company/components/Notice/CkEditorInput";
import BreadCrumb from "@/shared/components/BreadCrumb/BreadCrumb";

export default function CreateDeptKpi() {
  const history = useHistory();
  const id = history?.location?.state?.dept;
  const kpiId = history?.location?.state?.id;
  const {
    data: kpiData,
    loading: KpiLoading,
    error,
  } = useQuery(GET_DEPARTMENT_KPI_BY_ID, {
    variables: { id: kpiId },
    skip: !kpiId,
    onCompleted: (data) => {
      const kpi = data?.getDepartmentKpiById;
      if (kpi) {
        reset({
          key_area: kpi.key_area,
          description: kpi.description,
          dept_id: kpi.dept_id,
          weight: kpi.weight,
        });
      }
    },
  });
  const { data } = useQuery(GET_ALL_DEPARTMENT);
  const allDept = data?.getAllDepartments?.data || [];

  const deptOptions = allDept.map((dept) => ({
    value: dept.id,
    label: dept.name,
  }));

  const defaultDeptId = id;

  const [createDepartmentKpi, { loading }] = useMutation(
    CREATE_DEPARTMENT_KPI,
    {
      refetchQueries: [
        {
          query: GET_ALL_KPI_BY_DEPARTMENT_ID,
          variables: { dept_id: id },
        },
      ],
    }
  );
  const [updateDepartmentKpi, { loading: updating }] = useMutation(
    UPDATE_DEPARTMENT_KPI,
    {
      refetchQueries: [
        {
          query: GET_ALL_KPI_BY_DEPARTMENT_ID,
          variables: { dept_id: id },
        },
      ],
    }
  );
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      dept_id: defaultDeptId,
      key_area: kpiData?.getDepartmentKpiById?.key_area,
      description: kpiData?.getDepartmentKpiById?.description,
      weight: kpiData?.getDepartmentKpiById?.weight,
    },
  });

  const onSubmit = async (data) => {
    const formData = {
      id: Number(kpiId),
      key_area: data.key_area,
      description: data.description,
      dept_id: Number(data.dept_id),
      weight: data.weight,
    };

    try {
      if (kpiId) {
        const { data: updated } = await updateDepartmentKpi({
          variables: formData,
        });
        toast.success("KPI updated successfully!");
        history.goBack();
      } else {
        const { data: created } = await createDepartmentKpi({
          variables: {
            key_area: formData.key_area,
            description: formData.description,
            dept_id: formData.dept_id,
            weight: formData.weight,
          },
        });

        toast.success("KPI created successfully!");
        history.goBack();
      }

      reset(); // Clear the form after success
    } catch (err) {
      console.error("❌ KPI mutation failed:", err);
      toast.error("Failed to save KPI.");
    }
  };

  //   const breadCrumbData = [
  //     {
  //       name: "Dashboard",
  //       link: "/",
  //     },
  //     {
  //       name: "Kpi List",
  //       link: "/department/list",
  //     },
  //     // {
  //     //   name: "Department",
  //     //   link: "department/list",
  //     // },
  //     // {
  //     //   name: updateUserInfo?.id ? "Update User" : "Create User",
  //     // },
  //   ];

  if (KpiLoading) {
    return <p>loading</p>;
  }
  return (
    <div>
      {
        <Col md={12} lg={12} xl={12}>
          <Row className="mb-2">
            {/* <Col>
              <BreadCrumb breadCrumbData={breadCrumbData} />
            </Col> */}
            <h2 className="ml-2">{kpiId ? "Update Kpi" : "Create Kpi"}</h2>
          </Row>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Card>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label className="form__form-group-label">
                          Key Area
                        </Label>
                        <Controller
                          name="key_area"
                          control={control}
                          rules={{ required: "Key Area is required" }}
                          render={({ field }) => (
                            <Input
                              type="text"
                              placeholder="Enter key area"
                              value={field.value ?? ""}
                              onChange={(e) => field.onChange(e.target.value)}
                            />
                          )}
                        />
                        {errors.key_area && (
                          <small className="text-danger">
                            {errors.key_area.message}
                          </small>
                        )}
                      </FormGroup>
                    </Col>

                    {/* Department (Disabled) */}
                    <Col md={3}>
                      <FormGroup>
                        <Label className="form__form-group-label">
                          Department
                        </Label>
                        <Controller
                          name="dept_id"
                          control={control}
                          rules={{ required: "Department is required" }}
                          render={({ field }) => (
                            <Input
                              type="select"
                              className="form-control"
                              disabled
                              value={field.value ?? ""}
                              onChange={(e) =>
                                field.onChange(Number(e.target.value))
                              }
                            >
                              <option value="" disabled>
                                Select a department
                              </option>
                              {deptOptions.map((dept) => (
                                <option key={dept.value} value={dept.value}>
                                  {dept.label}
                                </option>
                              ))}
                            </Input>
                          )}
                        />
                        {errors.dept_id && (
                          <small className="text-danger">
                            {errors.dept_id.message}
                          </small>
                        )}
                      </FormGroup>
                    </Col>

                    {/* Weight */}
                    <Col md={3}>
                      <FormGroup>
                        <Label>Weight</Label>
                        <Controller
                          name="weight"
                          control={control}
                          rules={{ required: "Weight is required" }}
                          render={({ field }) => (
                            <Input
                              type="number"
                              step="any"
                              placeholder="Enter weight"
                              value={field.value ?? ""}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value === ""
                                    ? null
                                    : parseFloat(e.target.value)
                                )
                              }
                            />
                          )}
                        />
                        {errors.weight && (
                          <small className="text-danger">
                            {errors.weight.message}
                          </small>
                        )}
                      </FormGroup>
                    </Col>

                    {/* Description */}
                    <Col md={12}>
                      <FormGroup>
                        <Label className="form__form-group-label">
                          Description
                        </Label>
                        <Controller
                          name="description"
                          control={control}
                          rules={{ required: "Description is required" }}
                          render={({ field }) => (
                            <CkEditorInput
                              onChange={field.onChange}
                              value={field.value}
                            />
                          )}
                        />
                        {errors.description && (
                          <small className="text-danger">
                            {errors.description.message}
                          </small>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  {/* Submit */}
                  <Col md={12}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "end",
                        marginTop: "10px",
                      }}
                    >
                      <ButtonToolbar className="form__button-toolbar">
                        <Button
                          color="danger"
                          onClick={() => history.goBack()}
                          type="button"
                        >
                          Cancel
                        </Button>

                        <Button
                          color="primary"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? "Submitting..." : "Submit"}
                        </Button>
                      </ButtonToolbar>
                    </div>
                  </Col>
                </CardBody>
              </Card>
            </Row>
          </form>
        </Col>
      }
    </div>
  );
}
